import React, { useState, useEffect, useRef } from 'react';
import checkInImg from '../../assets/checkinImg.jpg';

const CheckInComponent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const componentRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    
    if (componentRef.current) {
      observer.observe(componentRef.current);
    }
    
    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, []);

  return (
    <div ref={componentRef} className="w-full bg-white py-12 md:py-24 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-teal-500 opacity-5"></div>
      <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-lime-400 opacity-5"></div>
      
      {/* SVG pattern */}
      <div className="absolute top-1/4 right-0 w-20 h-40 opacity-10 pointer-events-none">
        <svg width="100%" height="100%" viewBox="0 0 100 200" xmlns="http://www.w3.org/2000/svg">
          <path d="M30 30C40 10 60 0 80 20C100 40 90 70 70 90C50 110 30 100 10 80C-10 60 0 30 20 10C40 -10 20 50 30 30Z" fill="#00B2C2" />
        </svg>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col-reverse md:flex-row items-center justify-between gap-12">
        {/* Left content - Image */}
        <div className={`w-full md:w-1/2 relative transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
          {/* Image container with decorative elements */}
          <div className="relative z-10">
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-lime-400 rounded-full opacity-20"></div>
            <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-teal-500 rounded-full opacity-20"></div>
            
            <div className="relative rounded-xl overflow-hidden shadow-xl">
              <img 
                src={checkInImg} 
                alt="Staff member handing iPad to patient" 
                className="w-full h-auto transform transition-transform duration-700 hover:scale-105"
              />
              
              {/* Feature badge */}
              <div className="absolute top-4 right-4 bg-teal-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-md">
                5-Min Average Wait
              </div>
            </div>
          </div>
          
          {/* Interactive elements beneath image */}
          <div className="mt-6 flex items-center justify-center space-x-4">
            <div className={`flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-md transition-all duration-500 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '400ms' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 6v6l4 2" stroke="#00B2C2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="10" stroke="#00B2C2" strokeWidth="2"/>
              </svg>
            </div>
            
            <div className={`flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-md transition-all duration-500 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '500ms' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11l3 3L22 4" stroke="#A6CE39" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="#A6CE39" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <div className={`flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-md transition-all duration-500 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '600ms' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="4" width="16" height="16" rx="2" stroke="#00B2C2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 11v6" stroke="#00B2C2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 8v.01" stroke="#00B2C2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 16v-5" stroke="#00B2C2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 16v-3" stroke="#00B2C2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
        
        {/* Right content */}
        <div className={`w-full md:w-1/2 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
          <div className="inline-block mb-2">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5v14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 12h14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="text-teal-600 font-medium">PATIENT EXPERIENCE</span>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl text-gray-800 mb-6 font-serif relative">
            Easy Automated Check-ins
            <div className="h-1 w-20 bg-lime-400 mt-3 transform transition-transform duration-700 ease-out scale-x-0 origin-left" style={{
              transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
              transitionDelay: '300ms'
            }}></div>
          </h1>
          
          <p className="text-gray-700 text-lg mb-8">
            Use our in-office iPads to check in and complete your patient
            forms. Our average wait time is around 5 minutes — although,
            with all of the amenities in our waiting room, you may wish to
            stay longer.
          </p>
          
          <div className="space-y-4 mb-8">
            <div className={`flex items-start transition-all duration-500 transform`} style={{
              transitionDelay: '400ms',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
            }}>
              <span className="flex-shrink-0 w-6 h-6 bg-lime-400 rounded-full flex items-center justify-center mr-3 mt-1">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12L10 17L19 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="text-gray-700">Quick digital form completion</span>
            </div>
            
            <div className={`flex items-start transition-all duration-500 transform`} style={{
              transitionDelay: '500ms',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
            }}>
              <span className="flex-shrink-0 w-6 h-6 bg-lime-400 rounded-full flex items-center justify-center mr-3 mt-1">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12L10 17L19 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="text-gray-700">Secure patient information handling</span>
            </div>
            
            <div className={`flex items-start transition-all duration-500 transform`} style={{
              transitionDelay: '600ms',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
            }}>
              <span className="flex-shrink-0 w-6 h-6 bg-lime-400 rounded-full flex items-center justify-center mr-3 mt-1">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12L10 17L19 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="text-gray-700">Comfortable waiting area amenities</span>
            </div>
          </div>
          
          <button className="relative overflow-hidden group bg-gradient-to-r from-teal-500 to-teal-600 text-white font-medium px-8 py-3 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg flex items-center">
            <span className="relative z-10">LEARN MORE</span>
            <svg className="ml-2 w-5 h-5 transition-transform duration-300 transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20 group-hover:animate-shine"></div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckInComponent;